import React, { useState, useEffect, useCallback } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import { useNavigate } from 'react-router-dom';
import { url } from '../../util/config';

const STATUS_MAP = {
    0: { label: 'Pending',     bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
    1: { label: 'In Progress', bg: '#eff6ff', color: '#1565c0', border: '#bfdbfe' },
    7: { label: 'Completed',   bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
};

const getStatus = (status) => STATUS_MAP[Number(status)] || { label: `Status ${status}`, bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };

const CampaignComponent = () => {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [campaignNameFilter, setCampaignNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const itemsPerPage = 10;

    const fetchCampaigns = useCallback(async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${url}/campaign`, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token }
            });
            if (!response.ok) throw new Error('Failed to fetch campaigns');
            const data = await response.json();
            setCampaigns(data);
        } catch (error) {
            console.error('Error fetching campaigns:', error);
            setError('Failed to load campaigns. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

    useEffect(() => { setCurrentPage(1); }, [campaignNameFilter, statusFilter, startDateFilter, endDateFilter]);

    const filteredCampaigns = campaigns.filter((campaign) => {
        const matchesName = campaignNameFilter === '' ||
            (campaign.campaign_name && campaign.campaign_name.toLowerCase().includes(campaignNameFilter.toLowerCase()));
        const matchesStatus = statusFilter === '' || Number(campaign.status) === Number(statusFilter);
        const matchesDateRange =
            (startDateFilter === '' || new Date(campaign.schedule_date) >= new Date(startDateFilter)) &&
            (endDateFilter === '' || new Date(campaign.schedule_date) <= new Date(endDateFilter));
        return matchesName && matchesStatus && matchesDateRange;
    });

    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedCampaigns = filteredCampaigns.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);

    const hasFilters = campaignNameFilter || statusFilter || startDateFilter || endDateFilter;

    const handleReset = () => {
        setCampaignNameFilter('');
        setStatusFilter('');
        setStartDateFilter('');
        setEndDateFilter('');
    };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>WhatsApp Campaigns</h5>
                        <p style={styles.pageSubtitle}>View and manage your campaigns</p>
                    </div>
                    <button style={styles.btnPrimary} onClick={() => navigate('/createCampaign')}>
                        <i className="bi bi-plus-lg" style={{ marginRight: 6 }}></i>
                        Add New Campaign
                    </button>
                </div>

                {/* Filters */}
                <div style={styles.filterCard}>
                    <div style={styles.filterGrid}>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Campaign Name</label>
                            <div style={styles.searchWrapper}>
                                <i className="bi bi-search" style={styles.searchIcon}></i>
                                <input
                                    type="text"
                                    style={styles.searchInput}
                                    value={campaignNameFilter}
                                    onChange={(e) => setCampaignNameFilter(e.target.value)}
                                    placeholder="Search campaigns…"
                                />
                            </div>
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Status</label>
                            <select style={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="">All Statuses</option>
                                <option value="0">Pending</option>
                                <option value="1">In Progress</option>
                                <option value="7">Completed</option>
                            </select>
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Start Date</label>
                            <input type="date" style={styles.filterInput} value={startDateFilter} onChange={(e) => setStartDateFilter(e.target.value)} />
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>End Date</label>
                            <input type="date" style={styles.filterInput} value={endDateFilter} onChange={(e) => setEndDateFilter(e.target.value)} />
                        </div>
                        {hasFilters && (
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <button style={styles.btnSecondary} onClick={handleReset}>
                                    <i className="bi bi-x" style={{ marginRight: 4 }}></i>Reset
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Table card */}
                <div style={styles.tableCard}>
                    <div style={styles.tableHeader}>
                        <h6 style={styles.tableTitle}>
                            <i className="bi bi-megaphone" style={{ marginRight: 8, color: '#1565c0' }}></i>
                            Campaigns
                        </h6>
                        <span style={styles.resultCount}>{filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''}</span>
                    </div>

                    {isLoading ? (
                        <div style={styles.centerState}>
                            <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }}></div>
                            <p style={styles.stateText}>Loading campaigns…</p>
                        </div>
                    ) : error ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-exclamation-circle" style={{ fontSize: 32, color: '#ef4444' }}></i>
                            <p style={{ ...styles.stateText, color: '#ef4444' }}>{error}</p>
                            <button style={styles.btnPrimary} onClick={fetchCampaigns}>Retry</button>
                        </div>
                    ) : filteredCampaigns.length === 0 ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-megaphone" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                            <p style={styles.stateText}>
                                {hasFilters ? 'No campaigns match your filters.' : 'No campaigns yet. Create your first campaign.'}
                            </p>
                            {!hasFilters && (
                                <button style={styles.btnPrimary} onClick={() => navigate('/createCampaign')}>Add New Campaign</button>
                            )}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHead}>
                                        <th style={styles.th}>Campaign Name</th>
                                        <th style={styles.th}>WABA No</th>
                                        <th style={styles.th}>Template</th>
                                        <th style={styles.th}>Created On</th>
                                        <th style={styles.th}>Scheduled</th>
                                        <th style={styles.th}>Status</th>
                                        <th style={styles.th}>Total</th>
                                        <th style={styles.th}>Duplicate</th>
                                        <th style={styles.th}>Processed</th>
                                        <th style={styles.th}>Success</th>
                                        <th style={styles.th}>Failed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedCampaigns.map((campaign) => {
                                        const st = getStatus(campaign.status);
                                        return (
                                            <tr key={campaign._id} style={styles.tr}>
                                                <td style={{ ...styles.td, fontWeight: '600' }}>{campaign.campaign_name}</td>
                                                <td style={styles.td}>{campaign.sender}</td>
                                                <td style={styles.td}>{campaign.template_name}</td>
                                                <td style={styles.td}>{campaign.createdAt}</td>
                                                <td style={styles.td}>{campaign.schedule_date}</td>
                                                <td style={styles.td}>
                                                    <span style={{ ...styles.badge, background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                                                        {st.label}
                                                    </span>
                                                </td>
                                                <td style={styles.td}>{campaign.total_base ?? '—'}</td>
                                                <td style={styles.td}>{campaign.duplicate_base ?? '—'}</td>
                                                <td style={styles.td}>{campaign.processed_count ?? '—'}</td>
                                                <td style={{ ...styles.td, color: '#16a34a', fontWeight: '600' }}>{campaign.success_count ?? '—'}</td>
                                                <td style={{ ...styles.td, color: '#dc2626', fontWeight: '600' }}>{campaign.failed_count ?? '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {!isLoading && !error && filteredCampaigns.length > 0 && (
                    <PaginationComponent currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                )}
            </div>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    filterCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px 20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    filterGrid: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 160 },
    filterLabel: { fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' },
    searchWrapper: { position: 'relative' },
    searchIcon: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '13px' },
    searchInput: { width: '100%', padding: '8px 10px 8px 32px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', color: '#374151' },
    filterSelect: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151', background: 'white' },
    filterInput: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151' },
    tableCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    tableHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f3f4f6' },
    tableTitle: { margin: 0, fontSize: '14px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center' },
    resultCount: { fontSize: '12px', color: '#6b7280', background: '#f3f4f6', padding: '3px 10px', borderRadius: '20px' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    tableHead: { background: '#eff6ff' },
    th: { padding: '11px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '2px solid #bfdbfe', whiteSpace: 'nowrap' },
    tr: { borderBottom: '1px solid #f3f4f6' },
    td: { padding: '11px 14px', color: '#374151', verticalAlign: 'middle', whiteSpace: 'nowrap' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { display: 'inline-flex', alignItems: 'center', padding: '8px 12px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};

export default CampaignComponent;
